import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { FiGlobe, FiUsers, FiInfo } from 'react-icons/fi';
import PulseLoader from 'react-spinners/PulseLoader';

import { Text, textVariantsEnum, fontWeightEnum } from '@components/Text';
import { Button, buttonVariants } from '@components/Button';
import { Dropdown, borderedSelectStyles } from '@components/Dropdown';
import { Callout, calloutVariants } from '@components/Callout';
import { addNotification, notificationStates } from '@components/Notification';
import {
  IconChipMultiSelect,
  iconChipMultiSelectTones,
} from '@components/IconChipMultiSelect';
import { useProductivityApps } from '@/hooks/useProductivityApps';

import { SettingsPanelHeader } from './SettingsPanelHeader.component';
import {
  SettingsFieldCard,
  settingsFieldCardLayouts,
  SETTINGS_FIELD_CONTROL_WIDTH,
} from './SettingsFieldCard.component';
import {
  resolveAppIcon,
  resolveUrlIcon,
  KNOWN_URL_OPTIONS,
} from './appsAndUrls.icons';

import styles from './AppsAndUrlsSection.module.scss';

const COMPANY_OPTION = { value: null, label: 'Company Defaults' };

const caseInsensitiveExclude = (list, exclude) =>
  list.filter(
    (item) =>
      !exclude.some((other) => other.toLowerCase() === item.toLowerCase())
  );

export const AppsAndUrlsSection = () => {
  const companyId = useSelector(
    (state) => state.auth.profile?.activeCompany?.companyId
  );
  const teamsData = useSelector((state) => state.team?.teams?.data ?? []);

  const contextOptions = useMemo(() => {
    const teamOptions = teamsData
      .filter((team) => !team.teamRemoved)
      .map((team) => ({ value: team.activeTeamid, label: team.Team }));
    return [COMPANY_OPTION, ...teamOptions];
  }, [teamsData]);

  const [selectedContext, setSelectedContext] = useState(COMPANY_OPTION);
  const selectedTeamId = selectedContext?.value ?? null;
  const isTeamContext = selectedTeamId != null;

  const {
    isLoading,
    isLoadingTeam,
    isSaving,
    hasChanges,
    knownAppNames,
    focusApps,
    distractionApps,
    setFocusApps,
    setDistractionApps,
    teamHasOverride,
    save,
    discardChanges,
    resetTeamOverride,
  } = useProductivityApps(companyId, selectedTeamId);

  // URLs have no backend/data model yet (unlike apps) — local-only state,
  // never persisted, and not tied to Scope since there's nothing to switch
  // between per-team yet.
  const [productiveUrls, setProductiveUrls] = useState([]);
  const [distractingUrls, setDistractingUrls] = useState([]);

  const productiveAppOptions = useMemo(
    () => caseInsensitiveExclude(knownAppNames, distractionApps),
    [knownAppNames, distractionApps]
  );
  const distractingAppOptions = useMemo(
    () => caseInsensitiveExclude(knownAppNames, focusApps),
    [knownAppNames, focusApps]
  );
  const productiveUrlOptions = useMemo(
    () => caseInsensitiveExclude(KNOWN_URL_OPTIONS, distractingUrls),
    [distractingUrls]
  );
  const distractingUrlOptions = useMemo(
    () => caseInsensitiveExclude(KNOWN_URL_OPTIONS, productiveUrls),
    [productiveUrls]
  );

  const handleSaveChanges = async () => {
    const res = await save();
    if (res.success) {
      addNotification({
        message: 'Productivity apps saved.',
        level: notificationStates.success,
      });
    } else {
      addNotification({
        message: res.error || 'Failed to save. Please try again.',
        level: notificationStates.error,
      });
    }
  };

  const handleCancel = () => {
    discardChanges();
    setProductiveUrls([]);
    setDistractingUrls([]);
  };

  const handleReset = async () => {
    const res = await resetTeamOverride();
    if (res.success) {
      addNotification({
        message: `${selectedContext.label} reset to company defaults.`,
        level: notificationStates.success,
      });
    } else {
      addNotification({
        message: res.error || 'Failed to reset. Please try again.',
        level: notificationStates.error,
      });
    }
  };

  return (
    <>
      <SettingsPanelHeader
        icon={<FiGlobe size={20} color="var(--indigo-600)" />}
        title="Productive Apps & URLs"
        description="Define which apps and websites are considered productive or distracting."
      />

      <SettingsFieldCard
        layout={settingsFieldCardLayouts.row}
        icon={<FiUsers size={16} color="var(--indigo-600)" />}
        label="Scope"
        helperText="Choose whether these settings apply to the entire company or a specific team."
      >
        <Dropdown
          options={contextOptions}
          value={selectedContext}
          onChange={setSelectedContext}
          isDisabled={hasChanges}
          width={SETTINGS_FIELD_CONTROL_WIDTH}
          selectStyles={borderedSelectStyles}
        />
      </SettingsFieldCard>

      {hasChanges && (
        <Text
          variant={textVariantsEnum.Text_xs}
          fontWeight={fontWeightEnum.Normal}
          color="warning-600"
        >
          Save or discard changes before switching scope.
        </Text>
      )}

      {isTeamContext && !isLoadingTeam && !teamHasOverride && (
        <Callout variant={calloutVariants.info} icon={<FiInfo size={14} />}>
          This team has no custom list yet. Company defaults are used until you
          add apps below and save.
        </Callout>
      )}

      {isLoading || isLoadingTeam ? (
        <div className={styles.loading}>
          <PulseLoader color="#3363f5" size={8} />
        </div>
      ) : (
        <>
          <div className={styles.fieldGroup}>
            <Text
              variant={textVariantsEnum.Text_md}
              fontWeight={fontWeightEnum.Semibold}
              color="gray-900"
            >
              Productive Apps
            </Text>
            <Text
              variant={textVariantsEnum.Text_sm}
              fontWeight={fontWeightEnum.Normal}
              color="gray-600"
            >
              Select apps that are considered productive.
            </Text>
            <IconChipMultiSelect
              options={productiveAppOptions}
              value={focusApps}
              onChange={setFocusApps}
              iconResolver={resolveAppIcon}
              tone={iconChipMultiSelectTones.productive}
              placeholder="Select productive apps"
              isDisabled={isSaving}
            />
          </div>

          <div className={styles.fieldGroup}>
            <Text
              variant={textVariantsEnum.Text_md}
              fontWeight={fontWeightEnum.Semibold}
              color="gray-900"
            >
              Distracting Apps
            </Text>
            <Text
              variant={textVariantsEnum.Text_sm}
              fontWeight={fontWeightEnum.Normal}
              color="gray-600"
            >
              Select apps that are considered distracting.
            </Text>
            <IconChipMultiSelect
              options={distractingAppOptions}
              value={distractionApps}
              onChange={setDistractionApps}
              iconResolver={resolveAppIcon}
              tone={iconChipMultiSelectTones.distracting}
              placeholder="Select distracting apps"
              isDisabled={isSaving}
            />
          </div>

          {/* <div className={styles.fieldGroup}>
            <Text
              variant={textVariantsEnum.Text_md}
              fontWeight={fontWeightEnum.Semibold}
              color="gray-900"
            >
              Productive URLs
            </Text>
            <Text
              variant={textVariantsEnum.Text_sm}
              fontWeight={fontWeightEnum.Normal}
              color="gray-600"
            >
              Select websites that are considered productive.
            </Text>
            <IconChipMultiSelect
              options={productiveUrlOptions}
              value={productiveUrls}
              onChange={setProductiveUrls}
              iconResolver={resolveUrlIcon}
              tone={iconChipMultiSelectTones.productive}
              placeholder="Select productive URLs"
            />
          </div>

          <div className={styles.fieldGroup}>
            <Text
              variant={textVariantsEnum.Text_md}
              fontWeight={fontWeightEnum.Semibold}
              color="gray-900"
            >
              Distracting URLs
            </Text>
            <Text
              variant={textVariantsEnum.Text_sm}
              fontWeight={fontWeightEnum.Normal}
              color="gray-600"
            >
              Select websites that are considered distracting.
            </Text>
            <IconChipMultiSelect
              options={distractingUrlOptions}
              value={distractingUrls}
              onChange={setDistractingUrls}
              iconResolver={resolveUrlIcon}
              tone={iconChipMultiSelectTones.distracting}
              placeholder="Select distracting URLs"
            />
          </div> */}

          <div className={styles.actionsRow}>
            {isTeamContext && teamHasOverride && (
              <button
                type="button"
                className={styles.resetLink}
                onClick={handleReset}
                disabled={isSaving}
              >
                Reset to company defaults
              </button>
            )}
            <Button
              text="Cancel"
              variant={buttonVariants.secondary}
              onClick={handleCancel}
              disabled={isSaving}
            />
            <Button
              text="Save Changes"
              variant={buttonVariants.primary}
              onClick={handleSaveChanges}
              loading={isSaving}
              disabled={!hasChanges}
            />
          </div>
        </>
      )}
    </>
  );
};

AppsAndUrlsSection.displayName = 'AppsAndUrlsSection';
